import { Form, Formik } from "formik";
import React from "react";
import * as yup from "yup";
import { usersRequestLogin, usersVerifyLogin } from "../../api/api";
import useApi from "../../hooks/useApi";
import { generateClassName } from "../../hooks/useAttributes";
import { useSession } from "../../state/api/session/useSession";
import { REQUEST_SOURCE } from "../../types";
import { RequestLoginRequest, VerifyOtpRequest } from "../../types/db/schema/sessions/session.schema";
import { Cookies, setCookie } from "../../util/cookies";
import { getExcelVersion } from "../../util/versioning";
import Button from "../components/buttons/Button";
import Submit from "../components/buttons/Submit";
import Page from "../components/container/Page";
import FieldWithLabel from "../components/formik/FieldWithLabel";
import AddinValueSlideshow from "../components/valueproposition/AddinValueSlideshow";
import "./LogInPage.css";

export default function LogInPage() {

  const [email, setEmail] = React.useState<string>();
  const [otpId, setOtpId] = React.useState<string>();

  const { mutate } = useSession();

  const callApi = useApi();
  const version = getExcelVersion();

  const requestOtp = async (values: RequestLoginRequest) => {
    const res = await callApi(usersRequestLogin(values));

    if (!res?.success) {
      return;
    }

    const { id } = res.data;

    setEmail(values.email);
    setOtpId(id);
  }

  const requestLoginForm = (
    <Formik
      initialValues={{
        email: "",
        source: version.isExcel ? REQUEST_SOURCE.EXCEL : REQUEST_SOURCE.WEB
      } satisfies RequestLoginRequest}
      validationSchema={yup.object().shape({
        email: yup.string().email().required("Please enter an email.")
      })}

      onSubmit={requestOtp}
    >
      {
        (formik) => (
          <Form className="login-form d-flex flex-column gap-3 w-100 p-3">
            <div className="d-flex flex-column gap-1">
              <h5 className="fw-bold m-0 p-0">Log in</h5>
              <span className="font-code font-s">with a one-time password</span>
            </div>
            <div className="d-flex flex-column gap-3 w-100">
              <FieldWithLabel bold autoComplete="email" name="email" type="email" label="Your Email" placeholder="Enter your email" />
              <div className="w-100 d-flex flex-row justify-content-between">
                <Button
                  variant="subtle"
                  text="Sign up FREE!"
                  onClick={async () => window.open("https://mfaunderwriting.com/free-tools", "_blank")}
                />
                <Submit icon="box-arrow-in-right" text="Request OTP" />
              </div>
            </div>
          </Form>
        )
      }
    </Formik>
  )

  const verifyLoginForm = (
    <Formik
      initialValues={{
        otp: "",
        email
      } satisfies Partial<VerifyOtpRequest>}
      onSubmit={async (values) => {

        const res = await callApi(usersVerifyLogin({
          ...values,
          requestId: otpId
        } as VerifyOtpRequest));

        if (!res?.success) {
          return;
        }

        const { token } = res.data;

        setCookie(Cookies.Session, token, 90);

        await mutate();
      }}
    >
      {
        (formik) => (
          <Form className="login-form d-flex flex-column gap-3 w-100 p-3">
            <div className="d-flex flex-column gap-1">
              <h5 className="fw-bold m-0 p-0">Verify your Email</h5>
              <span className="font-code font-s">Enter the one-time code sent to your email. The code expires in 10 minutes.</span>
            </div>
            <div className="d-flex flex-column gap-3 w-100">
              <div className="d-flex flex-row align-items-end gap-2">
                <FieldWithLabel
                  bold
                  name="otp"
                  className="w-100"
                  label="One-time Code"
                  placeholder="e.g. 123456"
                />
                <Button
                  variant="subtle"
                  text="Resend"
                  icon="arrow-clockwise"
                  onClick={async () => {
                    await requestOtp({
                      email: email!,
                      source: version.isExcel ? REQUEST_SOURCE.EXCEL : REQUEST_SOURCE.WEB
                    });
                    formik.resetForm();
                  }}
                />
              </div>
              <div className="w-100 d-flex flex-row justify-content-between">
                <Button
                  variant="subtle"
                  text="Back"
                  icon="chevron-left"
                  onClick={async () => {
                    setEmail(undefined);
                    setOtpId(undefined);
                  }}
                />
                <Submit icon="box-arrow-in-right" text="Log in" />
              </div>
            </div>
          </Form>
        )
      }
    </Formik>
  );

  const otpMode = !!email && !!otpId;

  return (
    <Page>
      <div className={generateClassName("log-in-page-bg transition", {
        value: otpMode,
        onTrue: "otp-bg"
      })} />
      <div className="d-flex w-100 align-items-center justify-content-center h-100">
        <div className="w-100 h-100 p-5 gap-5 d-flex flex-column align-items-center justify-content-center login-page-content">
          <div className={generateClassName("transition login-page-compartment align-self-start", {
            value: otpMode,
            onTrue: "login-page-compartment-hidden"
          })}>
            <AddinValueSlideshow />
          </div>
          {
            otpMode && (
              <div className="otp-container">
                {verifyLoginForm}
              </div>
            )
          }
          <div className={generateClassName("transition login-page-compartment align-self-end", {
            value: otpMode,
            onTrue: "login-page-compartment-hidden"
          })}>
            {requestLoginForm}
          </div>
        </div>
      </div>
    </Page >
  )
}
